import React, { FC, useEffect, useState, useMemo, useContext } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  ConfigContext,
  Language,
  UPSAPIContext,
  UPSContext,
} from "@app/provider";
import { SpinnerView } from "@app/components";
import { PaymentPlan } from "@app/postBill/models/apiControllerModel";
import { PaymentStatus } from "@app/ups/models";
import { useRequiredUpsSession } from "@app/ups/hooks";
import { routes } from "@app/routing";
import { logMsg } from "@app/utils/logMsg";
import UPSCreatePaymentForm from "../upsCreatePaymentForm";

const UPSCreatePaymentMethodContainer: FC = () => {
  const { config } = useContext(ConfigContext);
  const {
    loadingStates,
    createDirectPayment,
    createPaymentPlan,
    getPaymentRequest,
  } = useContext(UPSAPIContext);
  const { selectedUPSPaymentPlan } = useContext(UPSContext);
  const { paymentRequestId } = useRequiredUpsSession();
  const { locale } = useIntl();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string>("");
  const [customerSessionSecret, setCustomerSessionSecret] =
    useState<string>("");

  const currentLocale = locale === "es" ? Language.ES : Language.EN;

  const publishableKey = config.preBill.postBillStripeKey;
  const stripePromise = useMemo(
    () => loadStripe(publishableKey, { locale: currentLocale }),
    [currentLocale]
  );

  const handleCreatePayment = async () => {
    try {
      const response = await createDirectPayment(paymentRequestId);
      setClientSecret(response.data?.clientSecret as string);
      setCustomerSessionSecret(response.data?.customerSessionSecret as string);
    } catch {
      logMsg("error", "ups.payment", "Error on handleCreatePayment");
    }
  };

  const handleCreatePaymentPlan = async () => {
    try {
      const response = await createPaymentPlan(paymentRequestId, {
        numberOfInstallments: selectedUPSPaymentPlan,
      });
      setClientSecret(response.data?.clientSecret as string);
      setCustomerSessionSecret(response.data?.customerSessionSecret as string);
    } catch {
      logMsg("error", "ups.payment", "Error on handleCreatePaymentPlan");
    }
  };

  useEffect(() => {
    const initPayment = async () => {
      try {
        const paymentRequest = await getPaymentRequest(paymentRequestId);
        const status = paymentRequest?.data?.status;

        if (
          status !== PaymentStatus.DRAFT &&
          status !== PaymentStatus.PROCESSING
        ) {
          navigate(routes.upsNoLongerAvailable, { replace: true });
          return;
        }

        if (selectedUPSPaymentPlan === PaymentPlan.None) {
          await handleCreatePayment();
        } else {
          await handleCreatePaymentPlan();
        }
      } catch {
        logMsg("error", "ups.payment", "Error on initPayment");
      }
    };

    initPayment();
  }, []);

  const options = {
    layout: {
      type: "accordion",
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
    paymentMethodOrder: ["apple_pay", "google_pay", "card"],
    clientSecret: clientSecret,
    customerSessionClientSecret: customerSessionSecret,
  };

  const elementsOptions = useMemo(() => options, [clientSecret]);

  return (
    <>
      <SpinnerView
        isLoading={
          loadingStates.createDirectPayment || loadingStates.getPaymentRequest
        }
      />
      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={elementsOptions}
          key={currentLocale}
        >
          <UPSCreatePaymentForm />
        </Elements>
      )}
    </>
  );
};

export default UPSCreatePaymentMethodContainer;
